<template>
    <!-- Title, field label, control label and button copy are verbatim from the UI handoff
         ("On-Demand Requests - UI Handoff", MODAL 1). -->
    <TprmModal
        open
        additional-class-name="CreateNewLinkModal"
        title="Request a new submission"
        :subtitle="subtitle"
        @close="close"
    >
        <!-- TODO PRODUCT-26539: D4 requires this modal to state that the entity moves to Pending
             Submission. Design has supplied no sentence for it, so none renders. -->
        <div v-if="showsRequirementPicker" class="CreateNewLinkModal__field">
            <div class="CreateNewLinkModal__fieldLabel">
                Select the requirements that need a new submission<span
                    class="CreateNewLinkModal__requiredMark"
                    aria-hidden="true"
                >*</span>
            </div>

            <p
                v-if="helperText"
                class="CreateNewLinkModal__helper"
                data-test-id="CreateNewLinkModal__helper"
            >
                {{ helperText }}
            </p>

            <div
                v-if="loading"
                class="CreateNewLinkModal__loading"
                data-test-id="CreateNewLinkModal__loading"
            >
                <EidIcon class="animate-spin" :icon-src="faSpinner" />
            </div>

            <div
                v-else
                class="CreateNewLinkModal__requirements"
                data-test-id="CreateNewLinkModal__requirements"
            >
                <div
                    v-for="row in requirementRows"
                    :key="row.key"
                    class="CreateNewLinkModal__requirement"
                    data-test-id="CreateNewLinkModal__requirement"
                >
                    <Checkbox
                        :model-value="isSelected(row.key)"
                        @update:model-value="setSelected(row.key, $event)"
                    >
                        <span class="CreateNewLinkModal__requirementText">
                            <span class="CreateNewLinkModal__requirementLabel">
                                {{ row.label }}
                                <span
                                    v-if="row.instanceLabel"
                                    class="CreateNewLinkModal__requirementInstance"
                                    data-test-id="CreateNewLinkModal__requirementInstance"
                                >
                                    {{ row.instanceLabel }}
                                </span>
                            </span>
                            <span
                                v-if="row.meta"
                                class="CreateNewLinkModal__requirementMeta"
                                data-test-id="CreateNewLinkModal__requirementMeta"
                            >
                                {{ row.meta }}
                            </span>
                        </span>
                    </Checkbox>
                </div>
            </div>
        </div>

        <div class="CreateNewLinkModal__notify" data-test-id="CreateNewLinkModal__notify">
            <Checkbox
                :model-value="notifyChecked"
                :disabled="entity.paused"
                @update:model-value="setNotifyEntity"
            >
                <span class="CreateNewLinkModal__notifyText">
                    <span class="CreateNewLinkModal__notifyLabel">Email this entity about the request</span>
                    <span
                        class="CreateNewLinkModal__notifyHelp"
                        data-test-id="CreateNewLinkModal__notifyHelp"
                    >
                        {{ notifyHelpText }}
                    </span>
                </span>
            </Checkbox>
        </div>

        <template #footer>
            <NewButton
                variant="outline"
                color="neutral"
                data-test-id="CreateNewLinkModal__cancel"
                @click="close"
            >
                Cancel
            </NewButton>
            <NewButton
                color="primary"
                class="CreateNewLinkModal__submitButton"
                data-test-id="CreateNewLinkModal__submit"
                :disabled="submitDisabled"
                :loading="saving"
                @click="confirm"
            >
                Create link
            </NewButton>
        </template>
    </TprmModal>
</template>

<script lang="ts">
    import { PropType } from 'vue';

    import { Component, Prop, Vue } from '@evidentid/vue-property-decorator';

    import startCase from 'lodash/startCase';

    import { faSpinner } from '@fortawesome/free-solid-svg-icons';

    import { Checkbox } from '@evidentid/dashboard-commons/components/Checkbox';
    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { ApiRequirementSelection } from '@evidentid/rpweb-api-client/models';
    import { TprmCredentialType } from '@evidentid/tprm-portal-lib/models/common';
    import { Entity } from '@evidentid/tprm-portal-lib/models/dashboard';
    import {
        ComplianceStatus,
        EntityRequirementDetails,
        TprmRequirementModel,
        TprmRequirementType,
    } from '@evidentid/tprm-portal-lib/models/entity-details';

    import NewButton from '@/components/new-button/NewButton.vue';
    import TprmModal from '@/components/tprm-modal/TprmModal.vue';
    import { formatDateOnly } from '@/modules/entity-events/utils/formatDateOnly';

    const COMPLIANT_HELPER = 'All of these requirements are currently satisfied. '
        + 'Only the ones you select will be reopened for the entity.';
    const NOTIFY_HELP_CHECKED = 'The request notification and the standard follow-up reminders will be scheduled.';
    const NOTIFY_HELP_UNCHECKED = 'No initial email and no follow-up reminders will be scheduled for this request. '
        + 'The link is still created.';
    // TODO PRODUCT-26539: design has not supplied a line for a paused entity. The unchecked line is
    // true of one but says nothing about why the choice is not on offer, so this names the reason.
    const NOTIFY_HELP_PAUSED = 'This entity\u2019s verification is paused, so no email or reminders will be '
        + 'sent. The link is still created.';

    export interface CreateNewLinkPayload {
        requirements?: ApiRequirementSelection[];
        notifyEntity: boolean;
    }

    interface RequirementRow {
        key: string;
        coverageType: TprmRequirementType;
        /** Identifies the instance to the API; `instanceLabel` is what the user reads. */
        instanceId?: string;
        instanceLabel?: string;
        label: string;
        meta: string;
    }

    // Declares every event this component `$emit`s -- without a declared `emits`, Vue 3 warns
    // "Component emitted event 'close'/'confirm' but it is neither declared in the emits option
    // nor as an 'onClose'/'onConfirm' prop" every time it fires.
    @Component({
        components: {
            Checkbox,
            EidIcon,
            NewButton,
            TprmModal,
        },
        emits: [ 'close', 'confirm' ],
    })
    export default class CreateNewLinkModal extends Vue {
        @Prop({ type: Object as PropType<Entity>, required: true })
        private entity!: Entity;

        @Prop({ type: Array as PropType<EntityRequirementDetails[]>, default: () => [] })
        private requirementDetails!: EntityRequirementDetails[];

        /** The RP's configured requirement models, carrying the label its users expect for this
         *  entity's country. */
        @Prop({ type: Array as PropType<TprmRequirementModel[]>, default: () => [] })
        private requirementModels!: TprmRequirementModel[];

        /** Credentials are the only multi-instance requirement so far, and an instance id is a
         *  UUID. Without these the rows of a multi-instance requirement are told apart by UUID,
         *  which is the one thing the instance-level picker exists to avoid. */
        @Prop({ type: Array as PropType<TprmCredentialType[]>, default: () => [] })
        private credentialTypes!: TprmCredentialType[];

        @Prop({ type: Boolean, default: false })
        private loading!: boolean;

        @Prop({ type: Boolean, default: false })
        private saving!: boolean;

        private faSpinner = faSpinner;

        // Suppressing the notification is the exception, so the toggle starts on.
        private notifyEntity: boolean = true;

        private selectedKeys: string[] = [];

        /** The handoff's subtitle is "{display name} · {contact email}"; the email is dropped
         *  rather than leaving a dangling separator when the entity has none. */
        private get subtitle(): string {
            return this.entity.contactEmail
                ? `${this.entity.displayName} \u00b7 ${this.entity.contactEmail}`
                : this.entity.displayName;
        }

        /** The picker is omitted, not disabled, for a non-compliant entity: the API rejects a
         *  `requirements` field for one outright, so a disabled picker would offer a capability
         *  that does not exist. */
        private get showsRequirementPicker(): boolean {
            return this.entity.complianceStatus === ComplianceStatus.compliant;
        }

        private get requirementRows(): RequirementRow[] {
            return this.requirementDetails.map((details) => ({
                key: this.rowKey(details),
                coverageType: details.coverageType,
                instanceId: details.instanceId || undefined,
                instanceLabel: this.instanceLabel(details),
                label: this.requirementLabel(details),
                meta: this.metaLine(details),
            }));
        }

        /** Prefers the RP's configured label, as the entity details panel does, and falls back to
         *  the coverage type so an unconfigured requirement still names itself. */
        private requirementLabel(details: EntityRequirementDetails): string {
            const configured = this.requirementModels
                .find((model) => model.coverageType === details.coverageType)?.label;
            return configured || startCase(details.coverageType.toLowerCase());
        }

        private instanceLabel(details: EntityRequirementDetails): string | undefined {
            if (!details.instanceId) {
                return undefined;
            }
            return this.credentialTypes
                .find((credentialType) => credentialType.id === details.instanceId)?.displayName
                || details.instanceId;
        }

        private get selectedRequirements(): ApiRequirementSelection[] {
            return this.requirementRows
                .filter((row) => this.selectedKeys.includes(row.key))
                .map((row) => (row.instanceId
                    ? { requirement: row.coverageType, instanceId: row.instanceId }
                    : { requirement: row.coverageType }));
        }

        /** Whether every row renders as "Satisfied", which is exactly what the helper line
         *  asserts about them.
         *
         *  Tested on `coverage` alone, deliberately matching `metaLine`: a row's meta reads
         *  "Satisfied" whenever coverage is present, and `complianceStatus` is nullable on
         *  `EntityRequirementDetails`, so testing it here made the helper disappear for rows that
         *  were simultaneously labelled Satisfied. One signal keeps the two from contradicting
         *  each other. D5's migrated entity -- assigned requirements with no coverage behind them
         *  -- still suppresses the line, which is the case the guard exists for. */
        private get everyRequirementSatisfied(): boolean {
            return this.requirementDetails.length > 0 &&
                this.requirementDetails.every((details) => Boolean(details.coverage));
        }

        private get helperText(): string {
            if (!this.showsRequirementPicker) {
                // TODO PRODUCT-26539: design has not supplied a helper line for a non-compliant
                // entity (spec §13 item 2b). The compliant line describes a picker this shape
                // omits and calls the entity satisfied, which it is not, so none renders.
                return '';
            }
            if (!this.everyRequirementSatisfied) {
                // TODO PRODUCT-26539: design has not supplied the variant for a migrated compliant
                // entity whose assigned requirements carry no coverage data (spec §13 item 2, D5).
                // "All of these requirements are currently satisfied" is false there.
                return '';
            }
            return COMPLIANT_HELPER;
        }

        /** A paused entity is never emailed -- the server leaves it unnotified by design -- so the
         *  choice is shown made rather than offered and then contradicted. */
        private get notifyChecked(): boolean {
            return !this.entity.paused && this.notifyEntity;
        }

        private get notifyHelpText(): string {
            if (this.entity.paused) {
                return NOTIFY_HELP_PAUSED;
            }
            return this.notifyChecked ? NOTIFY_HELP_CHECKED : NOTIFY_HELP_UNCHECKED;
        }

        private get submitDisabled(): boolean {
            // `saving` is checked here rather than relying on NewButton's own `disabled || loading`,
            // so the guard against a second submit is visible where the rule is stated.
            if (this.loading || this.saving) {
                return true;
            }
            return this.showsRequirementPicker && this.selectedRequirements.length === 0;
        }

        /** Coverage type alone is ambiguous for a multi-instance requirement, which contributes one
         *  row per instance. */
        private rowKey(details: EntityRequirementDetails): string {
            return details.instanceId
                ? `${details.coverageType} - ${details.instanceId}`
                : details.coverageType;
        }

        /** The meta copy design supplied speaks of a satisfied requirement. A row with no coverage
         *  -- D5's migrated entity -- has no design-supplied line, so it renders none. */
        private metaLine(details: EntityRequirementDetails): string {
            if (!details.coverage) {
                return '';
            }
            const expiration = formatDateOnly(details.coverage.policy?.expirationDate);
            return expiration ? `Satisfied · expires ${expiration}` : 'Satisfied · no expiration';
        }

        private isSelected(key: string): boolean {
            return this.selectedKeys.includes(key);
        }

        private setSelected(key: string, selected: boolean): void {
            this.selectedKeys = selected
                ? [ ...this.selectedKeys, key ]
                : this.selectedKeys.filter((selectedKey) => selectedKey !== key);
        }

        private setNotifyEntity(value: boolean): void {
            this.notifyEntity = value;
        }

        private confirm(): void {
            const payload: CreateNewLinkPayload = { notifyEntity: this.notifyChecked };
            if (this.showsRequirementPicker) {
                payload.requirements = this.selectedRequirements;
            }
            this.$emit('confirm', payload);
        }

        private close(): void {
            this.$emit('close');
        }
    }
</script>
